import { type ServerLifecycleWelcomePayload } from "@cmmd-forge/contracts";
import { scopedProjectKey, scopeProjectRef } from "@cmmd-forge/client-runtime";
import { Outlet, Navigate, createRootRouteWithContext, useLocation } from "@tanstack/react-router";
import { useEffect, useEffectEvent, useRef, type ReactElement } from "react";
import { QueryClient, useQueryClient } from "@tanstack/react-query";

import { APP_DISPLAY_NAME } from "../branding";
import { AppTopBarShell } from "../components/AppTopBar";
import { CloudSessionExpiredCoordinator } from "../components/CloudSessionExpiredCoordinator";
import { ThreadNotificationCoordinator } from "../components/ThreadNotificationCoordinator";
import { CommandPalette } from "../components/CommandPalette";
import { AppSidebarLayout } from "../components/AppSidebarLayout";
import { FirstAccountSetup } from "../components/onboarding/FirstAccountSetup";
import { FirstRunGate } from "../components/onboarding/FirstRunGate";
import {
  PluginConnectionsProvider,
  usePluginConnections,
} from "../components/plugins/PluginConnectionsProvider";
import { AuthenticatedDeviceRegistrationBootstrap } from "../components/AuthenticatedDeviceRegistrationBootstrap";
import { WebCloudAuthRedirectCoordinator } from "../components/WebCloudAuthRedirectCoordinator";
import {
  SlowRpcAckToastCoordinator,
  WebSocketConnectionCoordinator,
} from "../components/WebSocketConnectionSurface";
import { ThemeEditorHost } from "../components/settings/ThemeEditorHost";
import { OpenLocalHandoffCoordinator } from "../components/openLocal/OpenLocalHandoffCoordinator";
import { AnchoredToastProvider, ToastProvider, toastManager } from "../components/ui/toast";
import { resolveAndPersistPreferredEditor } from "../editorPreferences";
import { readLocalApi } from "../localApi";
import {
  getServerConfigUpdatedNotification,
  ServerConfigUpdatedNotification,
  startServerStateSync,
  useServerConfig,
  useServerConfigUpdatedSubscription,
  useServerWelcomeSubscription,
} from "../rpc/serverState";
import { useStore } from "../store";
import { useUiStateStore } from "../uiStateStore";
import { useAppearanceSurfaces, useTheme } from "../hooks/useTheme";
import { syncBrowserChromeTheme } from "../hooks/browserChromeTheme";
import { useStaleBuildWatchdog } from "../updateWatchdog/useStaleBuildWatchdog";
import {
  ensureEnvironmentConnectionBootstrapped,
  getPrimaryEnvironmentConnection,
  startEnvironmentConnectionService,
} from "../environments/runtime";
import { configureClientTracing } from "../observability/clientTracing";
import { bootstrapPosthog } from "../observability/posthog";
import {
  ensurePrimaryEnvironmentReady,
  resolveInitialServerAuthGateState,
  updatePrimaryEnvironmentDescriptor,
} from "../environments/primary";
import { useCmmdSync } from "~/hooks/useCmmdSync";
import { useCmmdUserSettingsSync } from "~/cmmd/userSettingsSyncClient";
import { syncWelcomePayloadForEventRouter } from "../routing/welcomePayloadSync";
import { rememberDesktopRoute } from "../lib/desktopRoutePersistence";
import { resolveKeybindingsConfigToastModel } from "./-rootRoute.logic";
import { RootRouteErrorView } from "./-RootRouteErrorView";
import { AuthenticatedAppSurface } from "./authenticatedAppSurface";

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  beforeLoad: async () => {
    const [descriptor, authGateState] = await Promise.all([
      ensurePrimaryEnvironmentReady(),
      resolveInitialServerAuthGateState(),
    ]);
    // Bootstrap PostHog so feature flags can evaluate before any gated UI
    // mounts. Identification happens inside bootstrapServerAuth alongside the
    // Sentry user setup — PostHog's identify is a no-op when not yet
    // initialized, so the order is safe: anonymous flag eval is fine as a
    // fallback for the brief window before the loaded callback fires.
    bootstrapPosthog({
      publicKey: descriptor.capabilities.posthogPublicKey,
      host: descriptor.capabilities.posthogHost,
    });
    return {
      authGateState,
      primaryEnvironmentDescriptor: descriptor,
    };
  },
  component: RootRouteView,
  errorComponent: RootRouteErrorView,
  head: () => ({
    meta: [{ name: "title", content: APP_DISPLAY_NAME }],
  }),
});

function RootRouteView() {
  return (
    <PluginConnectionsProvider>
      <RootRouteContent />
    </PluginConnectionsProvider>
  );
}

function RootRouteContent() {
  const pathname = useLocation({ select: (location) => location.pathname });
  const href = useLocation({ select: (location) => location.href });
  const { authGateState, primaryEnvironmentDescriptor } = Route.useRouteContext();
  const { theme, setTheme, uiFontSize, codeFontSize, setUiFontSize, setCodeFontSize } = useTheme();
  // The one mount. Every per-surface appearance preference is applied here or
  // it is applied nowhere — see `useAppearanceSurfaces`.
  useAppearanceSurfaces();
  // Root-level for the same reason: a deploy must reach every route, including
  // a tab parked on /login or a thread for days.
  useStaleBuildWatchdog();
  const showSetupPreview =
    import.meta.env.DEV === true && href.includes("firstAccountSetupPreview=1");
  const pluginConnections = usePluginConnections({ enabled: showSetupPreview });

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      syncBrowserChromeTheme();
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [pathname]);

  useEffect(() => {
    if (authGateState.status !== "authenticated") {
      return;
    }
    rememberDesktopRoute(window.location.hash || href);
  }, [authGateState.status, href]);

  // Mounted unconditionally above all branches so it runs on /login too.
  // This is required for the loop-break: after redirecting to /login, the
  // coordinator clears the cloudAuthFailureAtom so the next successful
  // CMMD login does not immediately re-fire the redirect.
  const webCloudAuthRedirect = <WebCloudAuthRedirectCoordinator />;

  if (showSetupPreview) {
    return (
      <>
        {webCloudAuthRedirect}
        <AppTopBarShell>
          <FirstAccountSetup
            accountLabel="Preview session"
            theme={theme}
            uiFontSize={uiFontSize}
            codeFontSize={codeFontSize}
            providers={[]}
            projects={[]}
            workspaceLabel="Forge development"
            plugins={pluginConnections.plugins}
            pluginsLoading={pluginConnections.loading}
            pluginsError={pluginConnections.error}
            pluginConnectionAttempt={pluginConnections.attempts.github ?? null}
            pluginConnectionAttempts={pluginConnections.attempts}
            onComplete={() => undefined}
            onPluginConnect={(pluginId) => void pluginConnections.connect(pluginId)}
            onThemeChange={setTheme}
            onUiFontSizeChange={setUiFontSize}
            onCodeFontSizeChange={setCodeFontSize}
          />
        </AppTopBarShell>
      </>
    );
  }

  if (pathname === "/pair" || pathname === "/login") {
    return (
      <>
        {webCloudAuthRedirect}
        <AppTopBarShell>
          <Outlet />
        </AppTopBarShell>
      </>
    );
  }

  // Onboarding overlays the workspace; unauthenticated browsers go to /login.
  if (pathname === "/welcome") {
    return (
      <WelcomeRouteShell
        authenticated={authGateState.status === "authenticated"}
        webCloudAuthRedirect={webCloudAuthRedirect}
      />
    );
  }

  if (authGateState.status !== "authenticated") {
    return (
      <>
        {webCloudAuthRedirect}
        <AppTopBarShell>
          <Navigate to="/login" replace />
        </AppTopBarShell>
      </>
    );
  }
  return (
    <ToastProvider>
      <AnchoredToastProvider>
        {webCloudAuthRedirect}
        {/* Data bootstraps feed the first-run decision above the gate; FirstRunGate holds everything below until it is known. */}
        <DataBootstraps />
        {/* The editor paints its draft live, so it survives navigation. */}
        <ThemeEditorHost />
        <FirstRunGate enabled={authGateState.status === "authenticated"}>
          <AuthenticatedTracingBootstrap />
          <AuthenticatedDeviceRegistrationBootstrap />
          <CmmdSyncBootstrap />
          <EventRouter />
          <WebSocketConnectionCoordinator />
          <CloudSessionExpiredCoordinator />
          <OpenLocalHandoffCoordinator />
          <SlowRpcAckToastCoordinator />
          <ThreadNotificationCoordinator />
          <AuthenticatedAppSurface
            pathname={pathname}
            primaryEnvironmentDescriptor={primaryEnvironmentDescriptor}
          />
        </FirstRunGate>
      </AnchoredToastProvider>
    </ToastProvider>
  );
}

/** The evidence producers the first-run decision reads; see the mount site above the gate. */
function DataBootstraps(): ReactElement {
  return (
    <>
      <CmmdUserSettingsSyncBootstrap />
      <EnvironmentConnectionManagerBootstrap />
      <ServerStateBootstrap />
    </>
  );
}

/**
 * The /welcome route shell: onboarding overlays the workspace; the data
 * bootstraps stay mounted for a direct load, gated coordinators suspended.
 */
function WelcomeRouteShell({
  authenticated,
  webCloudAuthRedirect,
}: {
  readonly authenticated: boolean;
  readonly webCloudAuthRedirect: ReactElement;
}): ReactElement {
  return (
    <>
      {webCloudAuthRedirect}
      {/* The wizard needs the data bootstraps mounted on a direct load. */}
      {authenticated ? <DataBootstraps /> : null}
      <ToastProvider>
        <AnchoredToastProvider>
          <CommandPalette>
            <AppSidebarLayout>
              <Outlet />
            </AppSidebarLayout>
          </CommandPalette>
        </AnchoredToastProvider>
      </ToastProvider>
    </>
  );
}

function ServerStateBootstrap() {
  useEffect(() => startServerStateSync(getPrimaryEnvironmentConnection().client.server), []);

  return null;
}

function AuthenticatedTracingBootstrap() {
  useEffect(() => {
    void configureClientTracing();
  }, []);

  return null;
}

function CmmdSyncBootstrap() {
  useCmmdSync();
  return null;
}

/** Hydrates and publishes the signed-in user's CMMD settings documents (forge#3515). */
function CmmdUserSettingsSyncBootstrap() {
  useCmmdUserSettingsSync();
  return null;
}

function EnvironmentConnectionManagerBootstrap() {
  const queryClient = useQueryClient();

  useEffect(() => {
    return startEnvironmentConnectionService(queryClient);
  }, [queryClient]);

  return null;
}

function EventRouter() {
  const setActiveEnvironmentId = useStore((store) => store.setActiveEnvironmentId);
  const seenServerConfigUpdateIdRef = useRef(getServerConfigUpdatedNotification()?.id ?? 0);
  const disposedRef = useRef(false);
  const serverConfig = useServerConfig();

  const handleWelcome = useEffectEvent((payload: ServerLifecycleWelcomePayload | null) => {
    void syncWelcomePayloadForEventRouter(payload, {
      ensureEnvironmentConnectionBootstrapped,
      isDisposed: () => disposedRef.current,
      setActiveEnvironmentId,
      setProjectExpanded: (environmentId, projectId) => {
        useUiStateStore
          .getState()
          .setProjectExpanded(scopedProjectKey(scopeProjectRef(environmentId, projectId)), true);
      },
      updateEnvironmentDescriptor: updatePrimaryEnvironmentDescriptor,
    }).catch(() => undefined);
  });

  const handleServerConfigUpdated = useEffectEvent(
    (notification: ServerConfigUpdatedNotification | null) => {
      const toastModel = resolveKeybindingsConfigToastModel({
        notification,
        seenNotificationId: seenServerConfigUpdateIdRef.current,
      });
      seenServerConfigUpdateIdRef.current = toastModel.nextSeenId;
      if (toastModel.kind === "ignore") {
        return;
      }
      if (toastModel.kind === "success") {
        toastManager.add({
          type: "success",
          title: toastModel.title,
          description: toastModel.description,
        });
        return;
      }

      toastManager.add({
        type: "warning",
        title: toastModel.title,
        description: toastModel.description,
        actionProps: {
          children: "Open keybindings.json",
          onClick: () => {
            const api = readLocalApi();
            if (!api) {
              return;
            }

            void Promise.resolve(serverConfig ?? api.server.getConfig())
              .then((config) => {
                const editor = resolveAndPersistPreferredEditor(config.availableEditors);
                if (!editor) {
                  throw new Error("No available editors found.");
                }
                return api.shell.openInEditor(config.keybindingsConfigPath, editor);
              })
              .catch((error) => {
                toastManager.add({
                  type: "error",
                  title: "Unable to open keybindings file",
                  description:
                    error instanceof Error ? error.message : "Unknown error opening file.",
                });
              });
          },
        },
      });
    },
  );

  useEffect(() => {
    if (!serverConfig) {
      return;
    }

    updatePrimaryEnvironmentDescriptor(serverConfig.environment);
    setActiveEnvironmentId(serverConfig.environment.environmentId);
  }, [serverConfig, setActiveEnvironmentId]);

  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
    };
  }, []);

  useEffect(() => {
    seenServerConfigUpdateIdRef.current = Math.max(
      seenServerConfigUpdateIdRef.current,
      getServerConfigUpdatedNotification()?.id ?? 0,
    );
  }, []);

  useServerWelcomeSubscription(handleWelcome);
  useServerConfigUpdatedSubscription(handleServerConfigUpdated);

  return null;
}
